/**
 * The position between now and expiry — ROADMAP 5.29.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EXPIRY CURVE IS ONE DAY OF THE ANSWER
 *
 * `basketPayoff` says what a basket settles at. Every decision before that
 * day is taken against what it is WORTH on the way — and a short straddle
 * that is comfortably inside its breakevens at expiry can still be deep in
 * the red tomorrow morning, because theta has not been collected yet and
 * volatility went the wrong way.
 *
 * So this reprices the whole basket on a grid: spot across a range, time
 * from today to expiry, at an implied volatility the reader can shift.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REPRICED ON THE FORWARD, WITH THE BASIS HELD
 *
 * Black-76 takes a forward, the grid is in SPOT, and the two differ by the
 * basis — carry, which does not move as spot is scanned across a chart. So
 * the basis measured now is held and the forward moves one for one with
 * spot, exactly as `groupGreeks` does for its T+0 line. Substituting the
 * cash index for the forward instead would tilt the whole surface by the
 * basis and show a straddle as skewed when it is not.
 *
 * EACH LEG KEEPS ITS OWN VOLATILITY, scaled by the multiplier. A single
 * number across the basket would flatten the smile, and the smile is most
 * of why a strangle and a straddle behave differently.
 *
 * THE LAST COLUMN IS INTRINSIC, NOT A 0.0001-YEAR BLACK-76. At expiry the
 * model divides by zero; `payoffAt` is the exact answer and is what the
 * chart's own curve uses, so the surface and the curve agree at the edge
 * they share.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { payoffAt } from './basketPayoff.js';
import { black76Price } from './greeks.js';
/**
 * What the basket is worth at a hypothetical spot, this many years out.
 *
 * `years` of zero is settlement and goes to intrinsic.
 */
export function valueAt(input, atSpot, yearsLeft) {
    const { legs, spot, forward, rate } = input;
    const mult = input.ivMultiplier ?? 1;
    if (!(atSpot > 0))
        return payoffAt(legs, Math.max(0, atSpot));
    if (!(yearsLeft > 0))
        return payoffAt(legs, atSpot);
    // Carry, measured now and held while spot is scanned.
    const basis = forward > 0 && spot > 0 ? forward - spot : 0;
    const atForward = atSpot + basis;
    let total = 0;
    for (const leg of legs) {
        if (leg.optionType === null || leg.strike === null) {
            // A future settles at spot and has no time value to lose.
            total += (atSpot - leg.avgPrice) * leg.netQty;
            continue;
        }
        const price = black76Price({
            forward: atForward,
            strike: leg.strike,
            years: yearsLeft,
            rate,
            vol: Math.max(leg.iv * mult, 0.0001),
            optionType: leg.optionType,
        });
        total += (price - leg.avgPrice) * leg.netQty;
    }
    return total;
}
const DAY = 1 / 365;
/**
 * A spot × time grid of the basket's P&L.
 *
 * Rows run HIGH TO LOW, the way a price ladder is drawn, so the grid reads
 * like the chain above it rather than upside down.
 *
 * The columns are today, a few days out, and expiry — chosen from the
 * days remaining rather than fixed, because a weekly with two days left
 * and a monthly with thirty need different steps to say anything.
 */
export function pnlMatrix(input, opts) {
    const rowCount = Math.max(3, opts.rows ?? 13);
    const { spot, years } = input;
    if (!(spot > 0) || input.legs.length === 0)
        return { columns: [], rows: [], scale: 1 };
    const daysLeft = Math.max(0, Math.round(years / DAY));
    const steps = [0, 1, 2, Math.max(3, Math.floor(daysLeft / 2))].filter((d, i, all) => d <= daysLeft && all.indexOf(d) === i);
    const columns = [
        ...steps.map((daysOut) => ({
            label: daysOut === 0 ? 'Today' : `+${daysOut}d`,
            daysOut,
        })),
        { label: 'Expiry', daysOut: daysLeft },
    ].filter((c, i, all) => all.findIndex((x) => x.daysOut === c.daysOut) === i);
    const span = (spot * opts.rangePct) / 100;
    const rows = [];
    let scale = 1;
    for (let i = 0; i < rowCount; i += 1) {
        // High to low.
        const at = spot + span - (2 * span * i) / (rowCount - 1);
        const cells = columns.map((c) => valueAt(input, at, Math.max(0, years - c.daysOut * DAY)));
        for (const c of cells)
            scale = Math.max(scale, Math.abs(c));
        rows.push({ spot: at, fromSpot: ((at - spot) / spot) * 100, cells });
    }
    return { columns, rows, scale };
}
/**
 * The chance the basket finishes in profit, from the breakevens.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A RISK-NEUTRAL PROBABILITY, AND IT SAYS SO WHEREVER IT IS SHOWN
 *
 * It is the lognormal weight of the profitable range under the market's own
 * implied volatility. That is the same quantity `probabilityItm` reports
 * per strike, and it is NOT a forecast: it is what the option prices imply,
 * which is a statement about the market and not about tomorrow.
 *
 * Returns null when it cannot be computed rather than a cheerful number —
 * no volatility, no time, or a profit region that is not a single interval.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function chanceOfProfit(input) {
    const { breakevens, spot, iv, years, insidePays } = input;
    if (!(spot > 0) || !(iv > 0) || !(years > 0))
        return null;
    const sigma = iv * Math.sqrt(years);
    // Lognormal cumulative at a price, under a zero-drift forward.
    const below = (price) => {
        if (!(price > 0))
            return 0;
        return normalCdf(Math.log(price / spot) / sigma + sigma / 2);
    };
    const sorted = [...breakevens].sort((a, b) => a - b);
    if (sorted.length === 0)
        return null;
    if (sorted.length === 1) {
        const p = below(sorted[0]);
        return round1((insidePays ? p : 1 - p) * 100);
    }
    if (sorted.length !== 2)
        return null;
    const inside = below(sorted[1]) - below(sorted[0]);
    return round1((insidePays ? inside : 1 - inside) * 100);
}
const round1 = (v) => Math.round(Math.max(0, Math.min(100, v)) * 10) / 10;
/** Abramowitz–Stegun, the same approximation `greeks.ts` uses. */
function normalCdf(x) {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const d = 0.3989422804014327 * Math.exp((-x * x) / 2);
    const p = d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
    return x > 0 ? 1 - p : p;
}
//# sourceMappingURL=payoffMatrix.js.map